<template>
  <app-overlay
    :model-value="modelValue"
    :size="size"
    :return-focus="returnFocus"
    :dismissible="false"
    :panel-class="$attrs.class"
    @close="handleRequestClose"
  >
    <header
      v-if="title || $slots.header || showClose"
      class="flex items-start justify-between gap-4 border-b border-theme-border px-5 py-4"
    >
      <slot name="header">
        <div class="min-w-0">
          <DialogTitle
            v-if="title"
            as="h2"
            class="font-semibold leading-6 text-theme-text"
            :class="titleClass || 'text-base'"
          >
            {{ title }}
          </DialogTitle>
          <DialogDescription
            v-if="description"
            as="p"
            class="mt-1 text-sm leading-5 text-theme-text-secondary"
          >
            {{ description }}
          </DialogDescription>
        </div>
      </slot>

      <button
        v-if="showClose"
        type="button"
        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-sm text-theme-text-secondary transition hover:bg-theme-bg-subtle hover:text-theme-text disabled:cursor-not-allowed disabled:opacity-50"
        :aria-label="closeLabel || 'Close dialog'"
        :disabled="confirmLoading"
        @click="cancel"
      >
        <icon :path="mdiClose" size="18" />
      </button>
    </header>

    <form-card ref="formCard" no-padding card-without-border rounded="" class="min-h-0 flex-1 !overflow-y-auto px-5 py-4" :content-class="bodyClass">
      <slot />
    </form-card>

    <footer
      v-if="$slots.footer || $slots['footer-start'] || showCancel || confirmLabel"
      class="flex flex-wrap items-center gap-2 border-t border-theme-border px-5 py-4"
      :class="$slots['footer-start'] ? 'justify-between' : 'justify-end'"
    >
      <slot name="footer">
        <span v-if="$slots['footer-start']" class="flex min-w-0 items-center">
          <slot name="footer-start" />
        </span>
        <span class="flex flex-wrap items-center justify-end gap-2">
          <app-button
            v-if="showCancel"
            :label="cancelLabel || t('general_button_cancel')"
            :disabled="confirmLoading"
            variant="outline"
            tone="muted"
            data-test="app-modal-cancel"
            @click="cancel"
          />
          <app-button
            v-if="confirmLabel"
            :label="confirmLabel"
            :loading="confirmLoading"
            :disabled="confirmDisabled"
            :tone="confirmTone"
            data-test="app-modal-confirm"
            @click="confirm"
          />
        </span>
      </slot>
    </footer>
  </app-overlay>
</template>

<script setup>
  /**
   * AppModal — standard modal (header + body + footer with confirm/cancel),
   * composed on top of the shared `AppOverlay` shell. The overlay owns teleport,
   * backdrop, centering, focus-trap, scroll-lock, Escape and the themed panel
   * container; AppModal adds the title/body/footer structure and the
   * confirm/cancel lifecycle.
   */
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppOverlay from '@/components/ui/overlays/AppOverlay.vue';
  import FormCard from '@/components/ui/layout/FormCard.vue';
  import { ref } from 'vue';
  import { DialogDescription, DialogTitle } from '@headlessui/vue';
  import { mdiClose } from '@mdi/js';
  import { useI18n } from 'vue-i18n';

  defineOptions({
    name: 'AppModal',
    inheritAttrs: false,
  });

  const props = defineProps({
    returnFocus: { type: Function, default: null },
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    /** Extra class on the title (e.g. 'text-xl' for a prominent heading). Overrides the default text-base size. */
    titleClass: { type: String, default: '' },
    description: { type: String, default: '' },
    /** Extra class on the scrollable body wrapper (e.g. 'space-y-3' to space multiple children). */
    bodyClass: { type: String, default: '' },
    confirmLabel: { type: String, default: '' },
    cancelLabel: { type: String, default: '' },
    closeLabel: { type: String, default: '' },
    confirmTone: { type: String, default: 'primary' },
    confirmLoading: { type: Boolean, default: false },
    confirmDisabled: { type: Boolean, default: false },
    showCancel: { type: Boolean, default: false },
    showClose: { type: Boolean, default: true },
    closeOnConfirm: { type: Boolean, default: true },
    closeOnBackdrop: { type: Boolean, default: true },
    closeOnEscape: { type: Boolean, default: true },
    size: { type: String, default: 'md' },
  });

  const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);
  const { t } = useI18n();
  const formCard = ref(null);

  function close(mode) {
    if (props.confirmLoading) return;
    emit('update:modelValue', false);
    emit(mode);
  }

  function confirm() {
    if (props.confirmLoading || props.confirmDisabled) return;
    if (formCard.value && !formCard.value.validate()) return;
    if (props.closeOnConfirm) {
      close('confirm');
      return;
    }
    emit('confirm');
  }

  function cancel() {
    close('cancel');
  }

  // AppOverlay forwards Headless' `@close` (Escape OR backdrop click). Headless
  // can't distinguish the two, so we honour the close if either is allowed; the
  // `confirmLoading` guard lives in `cancel()`.
  function handleRequestClose() {
    if (props.closeOnBackdrop || props.closeOnEscape) {
      cancel();
    }
  }
</script>
